import { Check, GripVertical, Info, Pin, Plus } from 'lucide-react'
import type { AssistantTurn, ChatTurn, Dashboard } from '../types'
import { InsightChart } from '../charts/InsightChart'
import { ChartErrorBoundary } from '../charts/ChartErrorBoundary'
import { SearchableSelect } from './SearchableSelect'
import { InfoTip } from './InfoTip'

function ThinkingDots() {
  return (
    <span className="dots" aria-label="Thinking">
      <span /> <span /> <span />
    </span>
  )
}

// MPD-79 — the board list is the shared SearchableSelect (MP-OS-189): with many boards it
// stays one searchable, keyboard-reachable list instead of a menu that overflows. Its
// face is the Pin pill. "New dashboard…" is its own action next to it.
// MPD-82 (MP-OS-193) — only the grip handle is draggable: the picker's search box and
// list sit outside any draggable ancestor, so opening/typing/selecting in them never
// starts a turn drag.
function PinControl({
  turn,
  pinned,
  dashboards,
  onPinTo,
  onDragStart,
  onDragEnd,
  preferredDashboardId,
}: {
  turn: AssistantTurn
  pinned: boolean
  dashboards: Dashboard[]
  onPinTo: (turn: AssistantTurn, target: string | 'new') => void
  onDragStart: (turn: AssistantTurn) => void
  onDragEnd: () => void
  preferredDashboardId?: string | null
}) {
  const preferred = dashboards.find((d) => d.id === preferredDashboardId)
  const face = pinned ? 'Added' : 'Pin'

  return (
    <div className="pin-wrap">
      {preferred && !pinned && (
        <button className="pin-btn" type="button" onClick={() => onPinTo(turn, preferred.id)}>
          <Pin size={15} /> Add to {preferred.name}
        </button>
      )}
      {dashboards.length > 0 && (
        <span className="pin-select-wrap">
          <button
            type="button"
            className="pin-drag-handle"
            draggable
            tabIndex={-1}
            onDragStart={() => onDragStart(turn)}
            onDragEnd={onDragEnd}
            aria-label="Drag to a dashboard in the sidebar"
            title="Drag to a dashboard in the sidebar"
          >
            <GripVertical size={14} aria-hidden />
          </button>
          <SearchableSelect
            className={`pin-btn pin-select ${pinned ? 'pinned' : ''}`}
            triggerClassName="pin-select-trigger"
            label="Pin to dashboard"
            searchLabel="Search dashboards"
            showAllLabel="Show all dashboards"
            title="Pin to a dashboard"
            options={dashboards.map((d) => ({ value: d.id, label: d.name }))}
            value=""
            placeholder={face}
            onChange={(id) => onPinTo(turn, id)}
            adornment={
              <>
                {pinned ? <Check size={15} aria-hidden /> : <Pin size={15} aria-hidden />}
                {face}
              </>
            }
          />
        </span>
      )}
      <button className="pin-btn pin-new" type="button" onClick={() => onPinTo(turn, 'new')}>
        <Plus size={14} aria-hidden /> New dashboard…
      </button>
    </div>
  )
}

export function ChatTurnView({
  turn,
  dashboards,
  pinned,
  onPinTo,
  onPinDragStart,
  onPinDragEnd,
  onRetry,
  preferredDashboardId,
}: {
  turn: ChatTurn
  dashboards: Dashboard[]
  pinned?: boolean
  onPinTo?: (turn: AssistantTurn, target: string | 'new') => void
  onPinDragStart?: (turn: AssistantTurn) => void
  onPinDragEnd?: () => void
  onRetry?: (turn: AssistantTurn) => void
  preferredDashboardId?: string | null
}) {
  if (turn.role === 'user') {
    return (
      <div className="turn user">
        <div className="bubble user-bubble">{turn.text}</div>
      </div>
    )
  }

  const inProgress = turn.status === 'thinking' || turn.status === 'streaming'

  return (
    <div className="turn assistant">
      <div className="bubble assistant-bubble">
        {turn.status !== 'error' && (
          <>
            {/* Narrative slot — streamed prose, or the working indicator until it starts. */}
            {turn.answer ? (
              <p className="answer-text">
                {turn.answer}
                {turn.status === 'streaming' && <span className="stream-cursor" aria-hidden />}
              </p>
            ) : (
              inProgress && (
                <div className="thinking">
                  <span className="ack">{turn.ack ?? 'Sure, let me check…'}</span>
                  <ThinkingDots />
                  {turn.steps.length > 0 && (
                    <span className="step-hint">{turn.steps[turn.steps.length - 1]}</span>
                  )}
                </div>
              )
            )}

            {/* Provenance slot, at commit only: the limits that change how the figures read
                stay visible; source and definitions (caveat_info) sit behind the ⓘ (MPD-67).
                An answer from before the split has only `caveat` and shows it as before. */}
            {turn.status === 'done' && (turn.caveat || turn.caveat_info) && (
              <div className="callout">
                <p>
                  {turn.caveat}
                  {turn.caveat_info && (
                    <InfoTip label="About this answer" size="small">{turn.caveat_info}</InfoTip>
                  )}
                </p>
              </div>
            )}

            {/* Chart slot — a skeleton holds the space while a chart is on its way;
                the real chart is mounted ONCE at commit, so it never swaps or vanishes. */}
            {turn.status === 'done'
              ? turn.chart && (
                  <ChartErrorBoundary>
                    <InsightChart chart={turn.chart} />
                  </ChartErrorBoundary>
                )
              : inProgress &&
                turn.chartPending && (
                  <div className="chart-skeleton" aria-hidden>
                    <div className="sk-bar" />
                    <div className="sk-bar" />
                    <div className="sk-bar" />
                    <div className="sk-bar" />
                    <div className="sk-bar" />
                  </div>
                )}

            {turn.status === 'done' && turn.preliminary && (
              <span className="prelim-badge">
                Preliminary period
                <InfoTip label="About the preliminary period" size="small">Contains an ongoing period — figures may change.</InfoTip>
              </span>
            )}
            {turn.status === 'done' && turn.pinnable !== false && onPinTo && onPinDragStart && onPinDragEnd && (
              <div className="turn-actions">
                <PinControl
                  turn={turn}
                  pinned={!!pinned}
                  dashboards={dashboards}
                  onPinTo={onPinTo}
                  onDragStart={onPinDragStart}
                  onDragEnd={onPinDragEnd}
                  preferredDashboardId={preferredDashboardId}
                />
              </div>
            )}
          </>
        )}

        {turn.status === 'error' && (
          <div className="notice" role="status">
            <Info size={16} className="notice-icon" />
            <p>{turn.error ?? 'I could not answer that.'}</p>
            {turn.question && onRetry && <button type="button" onClick={() => onRetry(turn)}>Try again</button>}
          </div>
        )}
      </div>
    </div>
  )
}
